// The frame both sign-in library screens share (amendment 2026-10-09): the scope (the org default
// or one installation; the selector is the AllCode admin's, REQ-214), the layered read, the draft,
// the checks and the write with the version read. A screen supplies only its cards, through an
// `Editor` over the same draft. The surface decides what the principal may edit (`fieldMode`);
// read-only mode (the API unreachable) disables the write and the list editors, as Models does.
// Installations are shown by name.
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import { ApiError } from "../../api/errors";
import type { OrgContext, Surface } from "../../api/org-context";
import { useHost } from "../../host/host";
import { useReadOnly } from "../../host/readonly";
import { hasErrors, type FieldErrors } from "../../primitives/forms";
import { StaleNote } from "../../primitives/stale-note";
import { ErrorBanner, Loading } from "../../primitives/states";
import { FieldRenderer } from "../../settings/field-renderer";
import { getPath, isInherited, merge, setPath, type Json, type JsonObject } from "../../settings/inherit";
import { fieldMode, pinFor, type FieldMode } from "../../settings/pins";
import { ScopeSelector, useDraftGuard, type ScopeOption } from "../../settings/scope-selector";
import { FIELDS, validate, type FieldDescriptor } from "../../settings/validate";
import { scopeId, type ModelsScope } from "../models/models-tab";
import { enabledMethods, isShown, loadScope, NO_METHOD_MESSAGE, saveFailureMessage, saveFieldErrors, saveScope, withLists, type Loaded } from "./model";
import { providerFromDoc, providersOf, type ProviderValues } from "./providers";
import { tokenFromDoc, tokensOf, validateTrustedTokens, type TrustedTokenValues } from "./tokens";

/** The scope a shell mounts a screen on: the org default, or one installation's override. */
export type SettingsScope = ModelsScope;

/** What a sign-in library screen takes from the shell that mounts it. */
export interface SignInScreenProps {
  context: OrgContext;
  /** The scope shown first; the AllCode admin's selector may move to another. */
  scope: SettingsScope;
}

const STALE_NOTE_ID = "signin-stale-note";
const DESCRIPTORS: ReadonlyMap<string, FieldDescriptor> = new Map(FIELDS.map((f) => [f.path, f]));

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

/** Whether `path` is one of `owned` or under one. */
function ownedBy(path: string, owned: readonly string[]): boolean {
  return owned.some((prefix) => path === prefix || path.startsWith(`${prefix}.`));
}

/** One screen's view of the draft: its cards read and edit through this. */
export interface Editor {
  /** "" is the org default; otherwise an installation id (never shown). */
  scope: string;
  loaded: Loaded;
  surface: Surface;
  /** The API is unreachable: the save and the list editors are disabled. */
  readOnly: boolean;
  effective: JsonObject;
  errors: FieldErrors;
  modeOf(path: string): FieldMode;
  /** One schema field over the settings form kit; null while the card hides it (`isShown`). */
  field(path: string): ComponentChildren;
  providers: readonly ProviderValues[];
  /** True while an installation shows the org default's providers, unedited. */
  providersInherited: boolean;
  setProviders(next: ProviderValues[]): void;
  tokens: readonly TrustedTokenValues[];
  setTokens(next: TrustedTokenValues[]): void;
}

export interface ScopedSettingsProps extends SignInScreenProps {
  /** The form's accessible name ("Sign-in settings"); an installation's name is added to it. */
  label: string;
  saveLabel: string;
  loadingLabel: string;
  /**
   * The paths this page edits: the errors it shows and blocks on are theirs, and its cards render
   * only their fields, so a save changes nothing outside them. Empty: a page with no Save.
   */
  owns: readonly string[];
  /** Told when the AllCode admin's selector moves to another scope, so the next sub-tab opens on it. */
  onScopeChange?(scope: SettingsScope): void;
  children(editor: Editor): ComponentChildren;
}

export function ScopedSettings({ context, scope: initialScope, label, saveLabel, loadingLabel, owns, onScopeChange, children }: ScopedSettingsProps) {
  const { orgId, surface } = context;
  const client = useApi();
  const host = useHost();
  const { readOnly } = useReadOnly();
  const [installations, setInstallations] = useState<ScopeOption[] | null>(null);
  const [scope, setScope] = useState(scopeId(initialScope));
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  // "Saved." lives here, not in the editor: a save reloads the scope, which remounts the
  // editor (keyed on the version) and would take its own status line with it (TASK-195).
  const [saved, setSaved] = useState(false);
  // The editor's draft differs from what was loaded: the scope selector asks before it moves.
  const [dirty, setDirty] = useState(false);

  const load = () => {
    setError(null);
    setLoaded(null);
    client
      .get("/v1/orgs/{orgId}/installations", { path: { orgId }, query: { limit: 500 } })
      .then((page) => setInstallations(page.items.map((item) => ({ id: item.id, label: item.name }))))
      .catch((err: unknown) => setError(unexpected(err)));
    loadScope(client, orgId, scope).then(setLoaded).catch((err: unknown) => setError(unexpected(err)));
  };
  useEffect(load, [client, orgId, scope]);

  if (error) return <ErrorBanner error={error} onRetry={load} />;
  if (!installations || !loaded) return <Loading label={loadingLabel} />;
  const scopeName = installations.find((i) => i.id === scope)?.label ?? "this installation";
  return (
    <>
      {readOnly && <StaleNote id={STALE_NOTE_ID} />}
      {host.kind === "allcode-admin" && (
        <ScopeSelector
          installations={installations}
          value={scope}
          dirty={dirty}
          onChange={(next) => {
            setSaved(false);
            setScope(next);
            onScopeChange?.(next === "" ? "org" : { installationId: next });
          }}
        />
      )}
      {saved && (
        <p class="field-hint" role="status">
          Saved.
        </p>
      )}
      <SettingsEditor
        key={`${scope}:${loaded.version}`}
        orgId={orgId}
        surface={surface}
        scope={scope}
        loaded={loaded}
        readOnly={readOnly}
        label={scope === "" ? label : `${label} for ${scopeName}`}
        saveLabel={saveLabel}
        owns={owns}
        onSaved={() => {
          setSaved(true);
          load();
        }}
        onEdited={() => setSaved(false)}
        onDirty={setDirty}
      >
        {children}
      </SettingsEditor>
    </>
  );
}

interface SettingsEditorProps {
  orgId: string;
  surface: Surface;
  scope: string;
  loaded: Loaded;
  readOnly: boolean;
  label: string;
  saveLabel: string;
  owns: readonly string[];
  onSaved(): void;
  onEdited(): void;
  /** Told whether the draft differs from what was loaded (the scope selector's guard). */
  onDirty(dirty: boolean): void;
  children(editor: Editor): ComponentChildren;
}

function SettingsEditor({ orgId, surface, scope, loaded, readOnly, label, saveLabel, owns, onSaved, onEdited, onDirty, children }: SettingsEditorProps) {
  const client = useApi();
  const [draft, setDraft] = useState<JsonObject>(loaded.document);
  // Null until edited, so an installation that never touches a list keeps inheriting it.
  const [providers, setProviders] = useState<ProviderValues[] | null>(null);
  const [tokens, setTokens] = useState<TrustedTokenValues[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const [failure, setFailure] = useState<ApiError | null>(null);

  const current = withLists(draft, providers, tokens);
  const effective = (merge(loaded.inherited ?? {}, current) as JsonObject | undefined) ?? {};
  const own = Object.fromEntries(Object.entries(validate(effective)).filter(([path]) => ownedBy(path, owns) && isShown(path, effective)));
  const errors: FieldErrors = { ...own, ...(tokens ? validateTrustedTokens(tokens) : {}), ...serverErrors };
  const noMethod = enabledMethods(effective) === 0;
  const changed = JSON.stringify(current) !== JSON.stringify(loaded.document);
  useDraftGuard(changed && !saving, onDirty);
  const blocked = readOnly || hasErrors(errors) || noMethod || saving || !changed;

  const modeOf = (path: string): FieldMode => {
    const descriptor = DESCRIPTORS.get(path);
    return descriptor ? fieldMode(descriptor, surface, pinFor(loaded.pins, path)) : { kind: "editable" };
  };
  const edited = () => {
    setServerErrors({});
    onEdited();
  };

  const editor: Editor = {
    scope,
    loaded,
    surface,
    readOnly,
    effective,
    errors,
    modeOf,
    field: (path) => {
      const descriptor = DESCRIPTORS.get(path);
      if (!descriptor || !isShown(path, effective)) return null;
      return (
        <FieldRenderer
          key={path}
          field={descriptor}
          value={getPath(effective, path)}
          mode={modeOf(path)}
          inherited={loaded.inherited !== undefined && isInherited(draft, path)}
          error={errors[path]}
          onChange={(next) => {
            setDraft((d) => setPath(d, path, next as Json | undefined));
            edited();
          }}
        />
      );
    },
    providers: providers ?? providersOf(effective).map(providerFromDoc),
    providersInherited: loaded.inherited !== undefined && providers === null && isInherited(draft, "sign_in.sso_providers"),
    setProviders: (next) => {
      setProviders(next);
      edited();
    },
    tokens: tokens ?? tokensOf(effective).map(tokenFromDoc),
    setTokens: (next) => {
      setTokens(next);
      edited();
    },
  };

  const save = () => {
    if (blocked) return;
    setSaving(true);
    setFailure(null);
    setServerErrors({});
    saveScope(client, orgId, scope, loaded.version, current)
      .then(onSaved)
      .catch((err: unknown) => {
        const api = unexpected(err);
        setServerErrors(saveFieldErrors(api));
        setFailure(api);
      })
      .finally(() => setSaving(false));
  };

  return (
    <form
      aria-label={label}
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      {children(editor)}
      {noMethod && (
        <p class="field-error" role="alert">
          {NO_METHOD_MESSAGE}
        </p>
      )}
      {failure && <p role="alert">{saveFailureMessage(failure.status, failure.code, failure.message)}</p>}
      {/* The one save stays in reach below the cards (a sticky bar, SIG-2b); a page that owns no
          path (Sign-in's Status & methods) has nothing to save. */}
      {owns.length > 0 && (
        <div class="form-actions-start settings-actions">
          <button type="submit" class="button button-primary" disabled={blocked} aria-describedby={readOnly ? STALE_NOTE_ID : undefined}>
            {saving ? "Saving…" : saveLabel}
          </button>
        </div>
      )}
    </form>
  );
}
